import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Users, UserCheck, UserX, Clock, Check, X, Loader2, Shield, RotateCcw, AlertTriangle } from 'lucide-react';
import ConcurrencyMonitor from '../components/admin/ConcurrencyMonitor';

const API = window.__API_BASE + '/api';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState({ pending: [], approved: [] });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [semaphoreLoading, setSemaphoreLoading] = useState(false);
  const [semaphoreResult, setSemaphoreResult] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { timeout: 10000 });
      setUsers(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        alert('Admin access required');
        navigate('/');
      }
      console.error('Admin users load failed:', err.message);
    }
    setLoading(false);
  };

  const approveUser = async (email) => {
    setActionLoading(email);
    try {
      await axios.post(`${API}/admin/users/${email}/approve`);
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to approve user');
    }
    setActionLoading(null);
  };

  const rejectUser = async (email) => {
    // confirm removed - non-blocking
    setActionLoading(email);
    try {
      await axios.post(`${API}/admin/users/${email}/reject`);
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reject user');
    }
    setActionLoading(null);
  };

  const toggleUserStatus = async (email, isActive) => {
    setActionLoading(email);
    try {
      const action = isActive ? 'disable' : 'enable';
      await axios.post(`${API}/admin/users/${email}/${action}`);
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update user');
    }
    setActionLoading(null);
  };

  const resetSemaphores = async () => {
    // confirm removed - non-blocking
    setSemaphoreLoading(true);
    setSemaphoreResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/reset-semaphores`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSemaphoreResult(res.data.reset || res.data.message);
    } catch (err) {
      setSemaphoreResult('Failed: ' + (err.response?.data?.detail || err.message));
    }
    setSemaphoreLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ai-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Admin Panel</h1>
                  <p className="text-xs text-gray-400">User Management</p>
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {user?.email}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{users.pending.length}</p>
                <p className="text-sm text-gray-500">Pending Approval</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{users.approved.length}</p>
                <p className="text-sm text-gray-500">Approved Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ai-bg-secondary flex items-center justify-center">
                <Users className="w-6 h-6 text-ai-accent" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{users.pending.length + users.approved.length}</p>
                <p className="text-sm text-gray-500">Total Users</p>
              </div>
            </div>
          </div>
        </div>

        {/* System Controls */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-6 overflow-hidden" data-testid="system-controls">
          <div className="p-5 border-b border-gray-100 bg-gray-50">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              System Controls
            </h2>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Reset Image Generation Semaphores</p>
                <p className="text-sm text-gray-500 mt-1">Unblocks stuck Sketch / MJ slots if the pipeline freezes. Safe to run anytime.</p>
              </div>
              <button
                onClick={resetSemaphores}
                disabled={semaphoreLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 transition-colors"
                data-testid="reset-semaphores-btn"
              >
                {semaphoreLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Reset Semaphores
              </button>
            </div>
            {semaphoreResult && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-gray-50 text-sm text-gray-600 font-mono space-y-1" data-testid="semaphore-result">
                {typeof semaphoreResult === 'string' ? (
                  <p>{semaphoreResult}</p>
                ) : (
                  <>
                    <p className="font-semibold text-gray-800 mb-1">All semaphores reset:</p>
                    <p><span className="text-gray-500">API:</span> {Object.entries(semaphoreResult.api_semaphores || {}).map(([k,v]) => `${k} ${v}`).join(', ')}</p>
                    <p><span className="text-gray-500">Stages:</span> {Object.entries(semaphoreResult.stage_semaphores || {}).map(([k,v]) => `${k} ${v}`).join(', ')}</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pending Approvals */}
        {users.pending.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-6 overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-amber-50">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Pending Approvals ({users.pending.length})
              </h2>
            </div>
            <div className="divide-y divide-gray-100">
              {users.pending.map((u) => (
                <div key={u.email} className="p-5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div>
                    <p className="font-medium text-gray-900">{u.name || u.email.split('@')[0]}</p>
                    <p className="text-sm text-gray-500">{u.email}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Registered: {new Date(u.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => approveUser(u.email)}
                      disabled={actionLoading === u.email}
                      className="px-5 py-2.5 bg-green-500 text-white rounded-full text-sm font-medium hover:bg-green-600 transition-all active:scale-95 flex items-center gap-2 shadow-sm"
                    >
                      {actionLoading === u.email ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Approve
                    </button>
                    <button
                      onClick={() => rejectUser(u.email)}
                      disabled={actionLoading === u.email}
                      className="px-5 py-2.5 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-all active:scale-95 flex items-center gap-2 shadow-sm"
                    >
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approved Users */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-500" />
              Approved Users ({users.approved.length})
            </h2>
          </div>
          {users.approved.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              No approved users yet
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {users.approved.map((u) => (
                <div key={u.email} className="p-5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${u.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{u.name || u.email.split('@')[0]}</p>
                        {u.is_admin && (
                          <span className="px-2 py-0.5 bg-ai-accent/10 text-ai-accent text-xs font-medium rounded-full">
                            Admin
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{u.email}</p>
                    </div>
                  </div>
                  {!u.is_admin && (
                    <button
                      onClick={() => toggleUserStatus(u.email, u.is_active)}
                      disabled={actionLoading === u.email}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all active:scale-95 ${
                        u.is_active
                          ? 'bg-gray-100 text-red-500 hover:bg-red-50'
                          : 'bg-green-50 text-green-600 hover:bg-green-100'
                      }`}
                    >
                      {actionLoading === u.email ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : u.is_active ? (
                        'Disable'
                      ) : (
                        'Enable'
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Concurrency Monitor */}
        <div className="mt-8">
          <ConcurrencyMonitor />
        </div>
      </main>
    </div>
  );
}
